/**
 * `validateMission` (03 §5.2): the hard gates every generated mission passes before it is
 * handed out. Works on a bare `MissionDef`, so saved or hand-built missions can be checked
 * too. Each message is one human-readable sentence; an empty list means valid.
 *
 * The quiet-path gate (./quiet) runs last, and only on a map that is otherwise sound: it is the
 * dearest check, and a map that fails another gate is re-rolled whatever it would have said.
 */

import type { MissionDef } from '../../sim/types';
import { ARCHETYPES } from '../archetypes';
import { sliceBands } from '../bands';
import type { Band } from '../types';
import { checkCaptured } from './captured';
import { checkDogs } from './dogs';
import { checkFairness } from './fairness';
import { checkBands, checkBarriers, checkProps } from './layout';
import { probeNav } from './nav';
import { checkQuietPath } from './quiet';
import { checkClock, checkConnectivity, checkStanding, standable } from './routes';
import { checkDogTags } from './tags';

function checkBasics(def: MissionDef): string[] {
  const out: string[] = [];
  if (!(def.sizeX >= 40 && def.sizeZ >= 100)) out.push(`the map is too small (${def.sizeX} x ${def.sizeZ})`);
  if (!(def.clockSeconds > 0)) out.push('the mission clock must be positive');
  if (!(def.threat >= 1 && def.threat <= 9)) out.push(`threat ${def.threat} is outside 1-9`);
  const band = def.dropBand;
  if (!(band.z0 < band.z1 && band.z0 > def.sizeZ / 2 && band.z1 <= def.sizeZ)) {
    out.push('the drop band must lie in the south of the map');
  }
  const g = def.goalZone;
  if (!(g.x0 < g.x1 && g.z0 < g.z1 && g.z1 < band.z0)) out.push('the goal zone must lie north of the drop band');
  if (!(def.pickup.z < band.z0)) out.push('the pickup must be north of the drop band');
  const ids = new Set(def.props.map((p) => p.id));
  for (const id of def.objective.targetPropIds) if (!ids.has(id)) out.push(`objective target ${id} is not a prop`);
  if (!(def.objective.minFraction > 0 && def.objective.minFraction <= 1)) out.push('minFraction must be in (0, 1]');
  if (!def.objective.rules.some((r) => r.type === 'recall')) out.push('the objective must say how Recall behaves');
  const captiveIds = new Set(def.captives.map((c) => c.id));
  if (captiveIds.size !== def.captives.length) out.push('captive ids must be unique');
  return out;
}

/**
 * The bands the generator used: they depend only on the seed and the map length, so they
 * can be recomputed. A mission that was not generated falls back to even slices.
 */
export function bandsOf(def: MissionDef): Band[] {
  const bands = sliceBands(def.seed, def.sizeZ);
  const drop = bands[0];
  if (drop && drop.z0 === def.dropBand.z0 && drop.z1 === def.dropBand.z1) return bands;
  const north = def.goalZone.z1 + 30;
  const count = Math.max(1, Math.round((def.dropBand.z0 - north) / 40));
  const depth = (def.dropBand.z0 - north) / count;
  const out: Band[] = [{ kind: 'drop', z0: def.dropBand.z0, z1: def.dropBand.z1 }];
  for (let i = 0; i < count; i++) {
    out.push({ kind: 'approach', z0: def.dropBand.z0 - depth * (i + 1), z1: def.dropBand.z0 - depth * i });
  }
  return out;
}

export function validateMission(def: MissionDef): string[] {
  const basics = checkBasics(def);
  if (basics.length > 0) return basics;
  const spec = ARCHETYPES[def.archetype];
  const nav = probeNav(def);
  const routes = checkConnectivity(def, nav, spec.breach);
  const bands = bandsOf(def);
  const faults = [
    ...checkProps(def),
    ...checkStanding(def, nav),
    ...routes.out,
    ...checkClock(def, nav),
    ...checkBarriers(def, nav),
    ...checkBands(def, nav, bands),
    ...checkFairness(def),
    ...checkCaptured(def, nav),
    ...checkDogTags(def, nav, bands),
    ...checkDogs(def, bands),
    ...spec.asserts(def, { breach: (id) => routes.breach.get(id) ?? -1, standable: (rect) => standable(nav, rect) }),
  ];
  return faults.length > 0 ? faults : checkQuietPath(def, nav, bands);
}
